import { Fragment, useState, useEffect, useCallback } from 'react';
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, Info } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { Switch } from '../../../../foundation/altzero_base/base/components/ui/switch';
import { ContractDrawer } from './ContractDrawer';
import { retailContractService, retailRateService } from '../../services/contractService';
import { getActiveRetailers, type Retailer } from '../../../scopingAi/services/retailerService';
import { CHARGE_UNIT_OPTIONS, RATE_ROWS, rateRowValue } from '../../utils/chargeNames';
import type {
  BvRetailContract,
  BvRetailContractFormData,
  BvRetailContractRate,
  BvRetailContractRateFormData,
  BvRateType,
  BvRateChargeUnit,
} from '../../types/billValidationTypes';

type Props = { siteId: string };

const EMPTY_CONTRACT: BvRetailContractFormData = {
  rate_type: 'smooth',
  is_current: true,
  retailer_name: '',
};

const EMPTY_RATE: BvRetailContractRateFormData = {
  // Master Data quotes Supply per servicePoint (columns R–T), so a new period
  // starts there rather than on the per-meter variant.
  supply_unit: 'per_servicepoint_day',
  // Metering fees are quoted annually far more often than daily, and the shadow
  // calculation has always treated a bare metering figure as $/year — so this is
  // the default that keeps existing rows billing the same way.
  metering_unit: 'per_meter_year',
  supplementary_metering_unit: 'per_meter_year',
  supplementary_services_unit: 'per_meter_year',
};

/** An amount paired with the unit it is quoted in. Entering one without the other
 *  is what left the rates table showing "$5" with no indication of per what. */
function ChargeWithUnit({
  label, amount, unit, onAmount, onUnit, defaultUnit = 'per_meter_year',
}: {
  label: string;
  amount: number | undefined;
  unit: BvRateChargeUnit | undefined;
  onAmount: (v: number | undefined) => void;
  onUnit: (v: BvRateChargeUnit) => void;
  defaultUnit?: BvRateChargeUnit;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>{label}</Label>
        <Input
          type="number"
          step="0.000001"
          value={amount ?? ''}
          onChange={(e) => onAmount(e.target.value === '' ? undefined : parseFloat(e.target.value))}
        />
      </div>
      <div>
        <Label>Unit</Label>
        <Select value={unit ?? defaultUnit} onValueChange={(v) => onUnit(v as BvRateChargeUnit)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHARGE_UNIT_OPTIONS.map((u) => (
              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function RetailContractSection({ siteId }: Props) {
  const [contracts, setContracts] = useState<BvRetailContract[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [contractDrawer, setContractDrawer] = useState(false);
  const [rateDrawer, setRateDrawer] = useState<{ open: boolean; contractId: string } | null>(null);
  const [editingContract, setEditingContract] = useState<BvRetailContract | null>(null);
  const [editingRate, setEditingRate] = useState<BvRetailContractRate | null>(null);
  const [contractForm, setContractForm] = useState<BvRetailContractFormData>(EMPTY_CONTRACT);
  const [rateForm, setRateForm] = useState<BvRetailContractRateFormData>(EMPTY_RATE);
  const [saving, setSaving] = useState(false);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [addingNewRetailer, setAddingNewRetailer] = useState(false);

  const load = useCallback(async () => {
    const r = await retailContractService.listBySite(siteId);
    if (r.success) setContracts(r.data ?? []);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getActiveRetailers().then((r) => { if (r.success) setRetailers(r.data ?? []); });
  }, []);

  const setC = <K extends keyof BvRetailContractFormData>(k: K, v: BvRetailContractFormData[K]) =>
    setContractForm((f) => ({ ...f, [k]: v }));
  const setR = <K extends keyof BvRetailContractRateFormData>(k: K, v: BvRetailContractRateFormData[K]) =>
    setRateForm((f) => ({ ...f, [k]: v }));

  const openAddContract = () => {
    setEditingContract(null);
    setContractForm(EMPTY_CONTRACT);
    setAddingNewRetailer(false);
    setContractDrawer(true);
  };

  const openEditContract = (c: BvRetailContract) => {
    setEditingContract(c);
    setContractForm({
      rate_type: c.rate_type,
      retailer_id: c.retailer?.id,
      retailer_name: c.retailer?.name ?? '',
      contract_start: c.contract_start,
      contract_end: c.contract_end,
      source: c.source,
      is_current: c.is_current,
    });
    // If this contract's retailer isn't in the validated list (e.g. legacy
    // free-text data), fall back to the manual-entry field so it's still visible.
    setAddingNewRetailer(!!c.retailer?.name && !retailers.some((r) => r.id === c.retailer?.id));
    setContractDrawer(true);
  };

  const saveContract = async () => {
    setSaving(true);
    if (editingContract) {
      await retailContractService.update(editingContract.id, contractForm);
    } else {
      await retailContractService.create(siteId, contractForm);
    }
    await load();
    setContractDrawer(false);
    setSaving(false);
  };

  const deleteContract = async (id: string) => {
    if (!confirm('Delete this contract and all its rates?')) return;
    await retailContractService.delete(id);
    await load();
  };

  const openAddRate = (contractId: string) => {
    setEditingRate(null);
    setRateForm(EMPTY_RATE);
    setRateDrawer({ open: true, contractId });
  };

  const openEditRate = (contractId: string, r: BvRetailContractRate) => {
    setEditingRate(r);
    setRateForm({
      period_start: r.period_start,
      period_end: r.period_end,
      peak_rate: r.peak_rate,
      shoulder_rate: r.shoulder_rate,
      off_peak_rate: r.off_peak_rate,
      anytime_rate: r.anytime_rate,
      super_peak_rate: r.super_peak_rate,
      supply_charge: r.supply_charge,
      supply_unit: r.supply_unit ?? 'per_meter_day',
      feed_in_rate: r.feed_in_rate,
      feed_in_rate_peak: r.feed_in_rate_peak,
      feed_in_rate_off_peak: r.feed_in_rate_off_peak,
      feed_in_rate_shoulder: r.feed_in_rate_shoulder,
      feed_in_rate_anytime: r.feed_in_rate_anytime,
      metering_rate: r.metering_rate,
      // Units were absent from this load, so reopening a rate and saving it wrote
      // the unit back as undefined even when one had been stored.
      metering_unit: r.metering_unit ?? 'per_meter_year',
      supplementary_metering_rate: r.supplementary_metering_rate,
      supplementary_metering_unit: r.supplementary_metering_unit ?? 'per_meter_year',
      supplementary_services_rate: r.supplementary_services_rate,
      supplementary_services_unit: r.supplementary_services_unit ?? 'per_meter_year',
      green_power_rate: r.green_power_rate,
    });
    setRateDrawer({ open: true, contractId });
  };

  const saveRate = async () => {
    if (!rateDrawer) return;
    setSaving(true);
    // On a smooth contract the rate spans the whole term, so the period dates are
    // hidden in the dialog — take the contract's own start/end instead.
    const parent = contracts.find((c) => c.id === rateDrawer.contractId);
    const payload: BvRetailContractRateFormData =
      parent?.rate_type === 'smooth'
        ? { ...rateForm, period_start: parent.contract_start, period_end: parent.contract_end }
        : rateForm;
    if (editingRate) {
      await retailRateService.update(editingRate.id, payload);
    } else {
      await retailRateService.create(rateDrawer.contractId, payload);
    }
    await load();
    setRateDrawer(null);
    setSaving(false);
  };

  const deleteRate = async (contractId: string, rateId: string) => {
    if (!confirm('Delete this rate period?')) return;
    await retailRateService.delete(contractId, rateId);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Retail Contracts</h3>
        <Button size="sm" variant="outline" onClick={openAddContract}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Contract
        </Button>
      </div>

      {contracts.length === 0 && (
        <p className="text-sm text-gray-400 py-2">No retail contracts</p>
      )}

      <div className="space-y-2">
        {contracts.map((c) => (
          <div key={c.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Contract header */}
            <div
              className="flex items-center justify-between px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpanded(expanded === c.id ? null : c.id)}
            >
              <div className="flex items-center gap-2">
                {expanded === c.id ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700">
                  {c.retailer?.name ?? 'Unknown retailer'}
                </span>
                <Badge variant="outline" className="text-xs capitalize">
                  {c.rate_type}
                </Badge>
                {c.is_current && (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                    Current
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                {c.contract_start && c.contract_end
                  ? `${new Date(c.contract_start).toLocaleDateString('en-AU')} → ${new Date(c.contract_end).toLocaleDateString('en-AU')}`
                  : ''}
                <button
                  onClick={(e) => { e.stopPropagation(); openEditContract(c); }}
                  className="p-1 rounded hover:bg-white hover:text-gray-600"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteContract(c.id); }}
                  className="p-1 rounded hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Rate periods */}
            {expanded === c.id && (
              <div className="p-3 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rate Periods</span>
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => openAddRate(c.id)}>
                    <Plus className="h-3 w-3 mr-1" /> Add Period
                  </Button>
                </div>
                {(c.rates ?? []).length === 0 ? (
                  <p className="text-xs text-gray-400">No rate periods</p>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-100">
                        <th className="pb-1.5 pr-4 whitespace-nowrap font-medium">Rate Type</th>
                        {(c.rates ?? []).map((r) => (
                          <th key={r.id} className="pb-1.5 pr-3 whitespace-nowrap font-medium">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1.5">
                                {(() => {
                                  // Smooth contracts hold no per-period dates — the rate spans
                                  // the whole contract term, so show the contract's own dates
                                  // instead of an em dash.
                                  const start = c.rate_type === 'smooth' ? c.contract_start : r.period_start;
                                  const end = c.rate_type === 'smooth' ? c.contract_end : r.period_end;
                                  return (
                                    <span>
                                      {start ? new Date(start).toLocaleDateString('en-AU') : '—'} →{' '}
                                      {end ? new Date(end).toLocaleDateString('en-AU') : '—'}
                                    </span>
                                  );
                                })()}
                                <div className="flex items-center gap-0.5">
                                  <button onClick={() => openEditRate(c.id, r)} className="p-0.5 hover:text-blue-600">
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                  <button onClick={() => deleteRate(c.id, r.id)} className="p-0.5 hover:text-red-600">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    {/* Rows follow the Master Data charge names and column order,
                        with the sheet's category as a group heading. */}
                    <tbody className="divide-y divide-gray-50">
                      {RATE_ROWS.map((row, i) => (
                        <Fragment key={row.label}>
                          {RATE_ROWS[i - 1]?.group !== row.group && (
                            <tr>
                              <td
                                colSpan={1 + (c.rates ?? []).length}
                                className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                              >
                                {row.group}
                              </td>
                            </tr>
                          )}
                          <tr className="hover:bg-gray-50">
                            <td className="py-1.5 pr-4 text-gray-500 font-medium whitespace-nowrap">{row.label}</td>
                            {(c.rates ?? []).map((r) => (
                              <td key={r.id} className="py-1.5 pr-3 whitespace-nowrap">{rateRowValue(row, r)}</td>
                            ))}
                          </tr>
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contract drawer */}
      <ContractDrawer
        open={contractDrawer}
        onClose={() => setContractDrawer(false)}
        title={editingContract ? 'Edit Retail Contract' : 'Add Retail Contract'}
      >
        <div className="space-y-4">
          {/* Retailer — validated select against the org's retailer list, so typos
              (e.g. "Mondu" instead of "Mondo") can't silently create a duplicate
              retailer row. "Add new retailer" is an explicit escape hatch. */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Retailer *</Label>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => {
                  setAddingNewRetailer((v) => !v);
                  setC('retailer_id', undefined);
                  setC('retailer_name', '');
                }}
              >
                {addingNewRetailer ? 'Choose existing retailer' : '+ Add new retailer'}
              </button>
            </div>
            {addingNewRetailer ? (
              <Input
                className="mt-1"
                placeholder="e.g. Origin Energy, AGL, EnergyAustralia"
                value={contractForm.retailer_name ?? ''}
                onChange={(e) => setC('retailer_name', e.target.value)}
              />
            ) : (
              <Select
                value={contractForm.retailer_id ?? ''}
                onValueChange={(v) => {
                  setC('retailer_id', v);
                  setC('retailer_name', retailers.find((r) => r.id === v)?.name ?? '');
                }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select retailer" /></SelectTrigger>
                <SelectContent>
                  {retailers.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Rate Type */}
          <div>
            <Label>Rate Type</Label>
            <Select value={contractForm.rate_type} onValueChange={(v) => setC('rate_type', v as BvRateType)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="smooth">Smooth</SelectItem>
                <SelectItem value="stepped">Stepped</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contract dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Contract Start</Label>
              <Input className="mt-1" type="date" value={contractForm.contract_start ?? ''} onChange={(e) => setC('contract_start', e.target.value)} />
            </div>
            <div>
              <Label>Contract End</Label>
              <Input className="mt-1" type="date" value={contractForm.contract_end ?? ''} onChange={(e) => setC('contract_end', e.target.value)} />
            </div>
          </div>

          {/* Source */}
          <div>
            <Label>Source</Label>
            <Input className="mt-1" value={contractForm.source ?? ''} onChange={(e) => setC('source', e.target.value)} placeholder="e.g. Signed contract, Online portal" />
          </div>

          {/* Is Current */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-gray-700">Current Contract</p>
              <p className="text-xs text-gray-400">Used for expected invoice calculations</p>
            </div>
            <Switch
              checked={contractForm.is_current ?? true}
              onCheckedChange={(v) => setC('is_current', v)}
            />
          </div>

          {/* Rate period hint */}
          {!editingContract && (
            <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                After saving, expand this contract and click <strong>Add Period</strong> to enter Peak, Off-Peak, and Supply Charge rates.
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={() => setContractDrawer(false)}>Cancel</Button>
            <Button className="flex-1" onClick={saveContract} disabled={saving}>
              {saving ? 'Saving…' : 'Save Contract'}
            </Button>
          </div>
        </div>
      </ContractDrawer>

      {/* Rate drawer */}
      <ContractDrawer
        open={rateDrawer?.open ?? false}
        onClose={() => setRateDrawer(null)}
        title={editingRate ? 'Edit Rate Period' : 'Add Rate Period'}
      >
        <div className="space-y-4">
          {/* Period dates — hidden for smooth contracts, where the rate applies for
              the whole contract term and the period inherits the contract's dates. */}
          {contracts.find((c) => c.id === rateDrawer?.contractId)?.rate_type === 'smooth' ? (
            <div className="flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
              <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-500 leading-relaxed">
                Smooth contract — this rate applies for the full contract term, so no period dates are needed.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={rateForm.period_start ?? ''} onChange={(e) => setR('period_start', e.target.value)} />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={rateForm.period_end ?? ''} onChange={(e) => setR('period_end', e.target.value)} />
              </div>
            </div>
          )}

          {/* Sections and labels follow the Master Data "New - BV Contract Rates"
              categories and charge names, in sheet column order. */}
          {/* Retail Charges — columns M–Q */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Retail Charges (¢/kWh)</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Peak</Label>
                <Input type="number" step="0.0001" value={rateForm.peak_rate ?? ''} onChange={(e) => setR('peak_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Shoulder</Label>
                <Input type="number" step="0.0001" value={rateForm.shoulder_rate ?? ''} onChange={(e) => setR('shoulder_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Off-Peak</Label>
                <Input type="number" step="0.0001" value={rateForm.off_peak_rate ?? ''} onChange={(e) => setR('off_peak_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Anytime</Label>
                <Input type="number" step="0.0001" value={rateForm.anytime_rate ?? ''} onChange={(e) => setR('anytime_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Super-Peak</Label>
                <Input type="number" step="0.0001" value={rateForm.super_peak_rate ?? ''} onChange={(e) => setR('super_peak_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Metering Charges — columns R–AA. Each charge carries its own unit;
              without one the rates table can only render a bare dollar amount. */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Metering Charges</p>
            <div className="space-y-2">
              <ChargeWithUnit
                label="Supply"
                amount={rateForm.supply_charge}
                unit={rateForm.supply_unit}
                defaultUnit="per_servicepoint_day"
                onAmount={(v) => setR('supply_charge', v)}
                onUnit={(v) => setR('supply_unit', v)}
              />
              <ChargeWithUnit
                label="Supplementary Metering"
                amount={rateForm.supplementary_metering_rate}
                unit={rateForm.supplementary_metering_unit}
                onAmount={(v) => setR('supplementary_metering_rate', v)}
                onUnit={(v) => setR('supplementary_metering_unit', v)}
              />
              <ChargeWithUnit
                label="Metering"
                amount={rateForm.metering_rate}
                unit={rateForm.metering_unit}
                onAmount={(v) => setR('metering_rate', v)}
                onUnit={(v) => setR('metering_unit', v)}
              />
              <ChargeWithUnit
                label="Supplementary Services"
                amount={rateForm.supplementary_services_rate}
                unit={rateForm.supplementary_services_unit}
                onAmount={(v) => setR('supplementary_services_rate', v)}
                onUnit={(v) => setR('supplementary_services_unit', v)}
              />
            </div>
          </div>

          {/* Environmental Charge — column AB */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Environmental Charge</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>GreenPower (¢/kWh)</Label>
                <Input type="number" step="0.000001" value={rateForm.green_power_rate ?? ''} onChange={(e) => setR('green_power_rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Solar Charges — columns AC–AF */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Solar Charges (¢/kWh)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Feed-in Anytime</Label>
                <Input type="number" step="0.000001" value={rateForm.feed_in_rate_anytime ?? ''} onChange={(e) => setR('feed_in_rate_anytime', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Feed-in Peak</Label>
                <Input type="number" step="0.000001" value={rateForm.feed_in_rate_peak ?? ''} onChange={(e) => setR('feed_in_rate_peak', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Feed-in Off-Peak</Label>
                <Input type="number" step="0.000001" value={rateForm.feed_in_rate_off_peak ?? ''} onChange={(e) => setR('feed_in_rate_off_peak', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
              <div>
                <Label>Feed-in Shoulder</Label>
                <Input type="number" step="0.000001" value={rateForm.feed_in_rate_shoulder ?? ''} onChange={(e) => setR('feed_in_rate_shoulder', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setRateDrawer(null)}>Cancel</Button>
            <Button className="flex-1" onClick={saveRate} disabled={saving}>
              {saving ? 'Saving…' : 'Save Period'}
            </Button>
          </div>
        </div>
      </ContractDrawer>
    </div>
  );
}
